import React, { useMemo, useState, useEffect } from "react";
import { Container, Row, Col, Card, Form } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import MetricCards from "../../../Components/CipAnalytics/MetricCards";
import OverviewTable from "../../../Components/CipAnalytics/OverviewTable";
import { ALERT_TYPE_MAPPING } from "../../../General/alertTypes";
import { navigateToBeveragesWithFilters } from "../../../utils/cipNavigationUtils";
import {
  groupDataByField,
  createGlobalRanges,
} from "../../../utils/cipDataUtils";
import { formatters } from "../../../constants/cipConstants";
import { useCipMetrics } from "../../../hooks/useCipMetrics";

export default function CipOverviewPage({
  filteredData,
  totalAlerts,
  totalTimeWaste,
  totalWaterWaste,
  totalChemicalWaste,
  cipTotalsData,
  convertVolume,
  convertTime,
  getVolumeUnit,
  getTimeUnit,
}) {
  const history = useHistory();
  const [selectedLeftAlert, setSelectedLeftAlert] = useState("");
  const [selectedRightAlert, setSelectedRightAlert] = useState("");

  // Create formatters with provided converters
  const currentFormatters = useMemo(
    () => ({
      ...formatters,
      volume: (liters) => {
        const converted = convertVolume(liters);
        if (typeof converted === "string") {
          const numValue = parseFloat(converted);
          return numValue.toLocaleString(undefined, {
            minimumFractionDigits: converted.includes(".") ? 1 : 0,
            maximumFractionDigits: converted.includes(".") ? 1 : 0,
          });
        }
        return converted.toLocaleString();
      },
      time: (seconds) => {
        const converted = convertTime(seconds);
        return converted.toLocaleString();
      },
    }),
    [convertVolume, convertTime]
  );

  const cipMetrics = useCipMetrics(filteredData, cipTotalsData);

  const handleTableRowClick = (row, tableType) => {
    const alertType = getAlertTypeForTable(tableType, row);

    // Build click data based on table type
    const clickData = {
      alertType: alertType,
      objectName: tableType === "issue" ? null : row.object_name,
    };

    navigateToBeveragesWithFilters(history, clickData);
  };

  const getAlertTypeForTable = (tableType, row) => {
    switch (tableType) {
      case "issue":
        return Object.keys(ALERT_TYPE_MAPPING).find(
          (id) => ALERT_TYPE_MAPPING[id] === row.issue
        );
      case "leftAlert":
        return selectedLeftAlert;
      case "rightAlert":
        return selectedRightAlert;
      case "object":
        return null;
      default:
        return null;
    }
  };

  // Calculate global ranges using utility
  const globalRanges = useMemo(
    () => createGlobalRanges(filteredData),
    [filteredData]
  );

  // Data groupings using utility functions
  const byIssueData = useMemo(
    () =>
      groupDataByField(filteredData, "alert_id").sort(
        (a, b) => b.alerts - a.alerts
      ),
    [filteredData]
  );

  const byObjectData = useMemo(
    () =>
      groupDataByField(filteredData, "object_name").sort(
        (a, b) => b.alerts - a.alerts
      ),
    [filteredData]
  );

  // Get most problematic alert types for default selection
  const mostProblematicAlerts = useMemo(() => {
    // Filter out alert type 80 (Unclassified) and get the top alerts
    const filteredByIssue = byIssueData.filter((item) => {
      const alertId = Object.keys(ALERT_TYPE_MAPPING).find(
        (id) => ALERT_TYPE_MAPPING[id] === item.issue
      );
      return alertId !== "80";
    });

    return filteredByIssue.slice(0, 2);
  }, [byIssueData]);

  // Set default selections when data changes
  useEffect(() => {
    if (mostProblematicAlerts.length >= 1 && !selectedLeftAlert) {
      const leftAlertId = Object.keys(ALERT_TYPE_MAPPING).find(
        (id) => ALERT_TYPE_MAPPING[id] === mostProblematicAlerts[0].issue
      );
      setSelectedLeftAlert(leftAlertId || "");
    }

    if (mostProblematicAlerts.length >= 2 && !selectedRightAlert) {
      const rightAlertId = Object.keys(ALERT_TYPE_MAPPING).find(
        (id) => ALERT_TYPE_MAPPING[id] === mostProblematicAlerts[1].issue
      );
      setSelectedRightAlert(rightAlertId || "");
    }
  }, [mostProblematicAlerts, selectedLeftAlert, selectedRightAlert]);

  // Bottom tables data with specific alert type filtering
  const byObjectForLeftAlertData = useMemo(() => {
    if (!selectedLeftAlert) return [];

    const filtered = filteredData.filter(
      (row) => row.alert_id === selectedLeftAlert
    );
    return groupDataByField(filtered, "object_name").sort(
      (a, b) => b.alerts - a.alerts
    );
  }, [filteredData, selectedLeftAlert]);

  const byObjectForRightAlertData = useMemo(() => {
    if (!selectedRightAlert) return [];

    const filtered = filteredData.filter(
      (row) => row.alert_id === selectedRightAlert
    );
    return groupDataByField(filtered, "object_name").sort(
      (a, b) => b.alerts - a.alerts
    );
  }, [filteredData, selectedRightAlert]);

  // Available alert types from data
  const uniqueAlertTypes = useMemo(
    () => [...new Set(filteredData.map((row) => row.alert_id).filter(Boolean))],
    [filteredData]
  );

  const alertTypeOptions = useMemo(
    () =>
      uniqueAlertTypes.map((type) => ({
        id: type,
        name: ALERT_TYPE_MAPPING[type] || `Unknown Alert Type ${type}`,
      })),
    [uniqueAlertTypes]
  );

  // Table columns configuration
  const columns = [
    {
      key: "alerts",
      label: "Alerts",
      type: "alerts",
      formatter: currentFormatters.alerts,
    },
    {
      key: "waterWaste",
      label: `Excessive Water (${getVolumeUnit()})`,
      type: "waterWaste",
      formatter: currentFormatters.volume,
    },
    {
      key: "chemicalWaste",
      label: `Chemical Loss (${getVolumeUnit()})`,
      type: "chemicalWaste",
      formatter: currentFormatters.volume,
    },
    {
      key: "timeWaste",
      label: `Time Loss`,
      type: "timeWaste",
      formatter: currentFormatters.time,
    },
  ];

  return (
    <Container fluid>
      {/* Metric Cards */}
      <MetricCards
        totalAlerts={totalAlerts}
        totalTimeWaste={totalTimeWaste}
        totalWaterWaste={totalWaterWaste}
        totalChemicalWaste={totalChemicalWaste}
        cipMetrics={cipMetrics}
        convertTime={convertTime}
        convertVolume={convertVolume}
        getVolumeUnit={getVolumeUnit}
        getTimeUnit={getTimeUnit}
        showSelection={false}
      />

      {/* Top Row - Summary Tables */}
      <Row className="mb-4">
        <Col lg={6}>
          <Card className="h-100">
            <Card.Header
              style={{
                backgroundColor: "transparent",
                border: "none",
                paddingBottom: "0",
              }}
            >
              <h5
                className="mb-0 text-left"
                style={{ color: "#374151", fontWeight: "600" }}
              >
                Impact per Issue
              </h5>
            </Card.Header>
            <Card.Body className="p-0">
              <OverviewTable
                data={byIssueData}
                columns={columns}
                globalRanges={globalRanges}
                rowKey="issue"
                rowLabel="Issue"
                emptyMessage="No alert data available"
                useTableSpecificRanges={true}
                isClickable={true}
                onRowClick={handleTableRowClick}
                tableType="issue"
              />
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="h-100">
            <Card.Header
              style={{
                backgroundColor: "transparent",
                border: "none",
                paddingBottom: "0",
              }}
            >
              <h5
                className="mb-0 text-left"
                style={{ color: "#374151", fontWeight: "600" }}
              >
                Impact per Object
              </h5>
            </Card.Header>
            <Card.Body className="p-0">
              <OverviewTable
                data={byObjectData}
                columns={columns}
                globalRanges={globalRanges}
                rowKey="object_name"
                rowLabel="Object"
                emptyMessage="No object data available"
                useTableSpecificRanges={true}
                isClickable={true}
                onRowClick={handleTableRowClick}
                tableType="object"
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Bottom Row - Drill-down Tables */}
      <Row>
        <Col lg={6}>
          <Card className="h-100">
            <Card.Header
              className="d-flex align-items-center justify-content-between"
              style={{
                backgroundColor: "transparent",
                border: "none",
                paddingBottom: "0",
              }}
            >
              <h5
                className="mb-0 text-left"
                style={{ color: "#374151", fontWeight: "600" }}
              >
                Impact per object related to:{" "}
                {ALERT_TYPE_MAPPING[selectedLeftAlert]}
              </h5>
              <Form.Control
                as="select"
                value={selectedLeftAlert}
                onChange={(e) => setSelectedLeftAlert(e.target.value)}
                style={{ width: "auto", maxWidth: "200px" }}
                size="md"
              >
                <option value="">Select Alert Type</option>
                {alertTypeOptions.map(({ id, name }) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </Form.Control>
            </Card.Header>
            <Card.Body className="p-0">
              <OverviewTable
                data={byObjectForLeftAlertData}
                columns={columns}
                globalRanges={globalRanges}
                rowKey="object_name"
                rowLabel="Object"
                emptyMessage={
                  selectedLeftAlert
                    ? `No objects found for ${ALERT_TYPE_MAPPING[selectedLeftAlert]}`
                    : "Select an alert type to see object breakdown"
                }
                useTableSpecificRanges={true}
                isClickable={true}
                onRowClick={handleTableRowClick}
                tableType="leftAlert"
              />
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="h-100">
            <Card.Header
              className="d-flex align-items-center justify-content-between"
              style={{
                backgroundColor: "transparent",
                border: "none",
                paddingBottom: "0",
              }}
            >
              <h5
                className="mb-0 text-left"
                style={{ color: "#374151", fontWeight: "600" }}
              >
                Impact per object related to:{" "}
                {ALERT_TYPE_MAPPING[selectedRightAlert]}
              </h5>
              <Form.Control
                as="select"
                value={selectedRightAlert}
                onChange={(e) => setSelectedRightAlert(e.target.value)}
                style={{ width: "auto", maxWidth: "200px" }}
                size="md"
              >
                <option value="">Select Alert Type</option>
                {alertTypeOptions.map(({ id, name }) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </Form.Control>
            </Card.Header>
            <Card.Body className="p-0">
              <OverviewTable
                data={byObjectForRightAlertData}
                columns={columns}
                globalRanges={globalRanges}
                rowKey="object_name"
                rowLabel="Object"
                emptyMessage={
                  selectedRightAlert
                    ? `No objects found for ${ALERT_TYPE_MAPPING[selectedRightAlert]}`
                    : "Select an alert type to see object breakdown"
                }
                useTableSpecificRanges={true}
                isClickable={true}
                onRowClick={handleTableRowClick}
                tableType="rightAlert"
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
